import { cn } from "@lib/cn";
import { closeDialog } from "@store/dialogs";
import { selectDialogs } from "@store/dialogs/selectors";
import type { TDialog } from "@store/dialogs/types";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Dialog from "@/components/ui/dialog";

type TClosingDialog = Pick<TDialog, "id" | "props"> & {
  isClosing: true;
  content?: never;
};

const DialogRoot = () => {
  "use no memo";
  const dialogs = useSelector(selectDialogs);
  const dispatch = useDispatch();
  const [openDialogs, setOpenDialogs] = useState<string[]>([]);

  useEffect(() => {
    setOpenDialogs((prev) => {
      const newDialogIds = dialogs
        .map((d) => d.id)
        .filter((id) => !prev.includes(id));
      if (newDialogIds.length > 0) {
        return [...prev, ...newDialogIds];
      }
      return prev;
    });
  }, [dialogs]);

  const visibleDialogs = [
    ...dialogs,
    ...openDialogs
      .filter((id) => !dialogs.some((d) => d.id === id))
      .map((id) => {
        // Find the original dialog to preserve its props
        const originalDialog = dialogs.find((d) => d.id === id);
        return {
          id,
          isClosing: true,
          props: originalDialog?.props,
        } as TClosingDialog;
      }),
  ];

  if (visibleDialogs.length === 0) return null;

  return (
    <>
      {visibleDialogs.map((dialog, index) => {
        let customZ: number | undefined;
        let closeOnOutsideClick: boolean | undefined;
        const autoZ = 10000 + index;

        if ("isClosing" in dialog) {
          // For closing dialogs, we don't have zIndex or closeOnOutsideClick
          customZ = undefined;
          closeOnOutsideClick = undefined;
        } else {
          // For active dialogs, we have full TDialog properties
          customZ = dialog.zIndex;
          closeOnOutsideClick = dialog.closeOnOutsideClick;
        }
        if (dialog.id.startsWith("media-lib")) {
          customZ = 20001;
          if (dialog.id.startsWith("media-lib-")) {
            customZ = 20002;
          }
        }

        if ("isClosing" in dialog) {
          const { id, props } = dialog;
          const className = props?.className as string;
          const overlayClassName = props?.overlayClassName as string;

          return (
            <Dialog
              key={id}
              open={false}
              onOpenChange={() => {}}
              closeOnOutsideClick={!!closeOnOutsideClick}
            >
              <Dialog.Content
                id={id}
                className={className}
                overlayClassName={overlayClassName}
                zIndex={customZ || autoZ}
                onAnimationEnd={() => {
                  setOpenDialogs((prev) =>
                    prev.filter((dialogId) => dialogId !== id),
                  );
                }}
              />
            </Dialog>
          );
        }

        const { id, content: Content, props } = dialog;
        const overlayClassName = cn(
          "overlayClassName" in Content
            ? (Content.overlayClassName as string)
            : undefined,
          props?.overlayClassName as string,
        );
        // Opt in per dialog, read off the content component like the overlay
        // class above. Escape was handled nowhere — floating-ui's dismiss is
        // disabled wholesale — and turning it on for every dialog at once would
        // start throwing away half-filled forms on a keystroke.
        const closeOnEscape =
          "closeOnEscape" in Content ? !!Content.closeOnEscape : false;

        return (
          <Dialog
            key={id}
            open={true}
            onOpenChange={(open) => {
              if (!open) dispatch(closeDialog(id));
            }}
            closeOnOutsideClick={!!closeOnOutsideClick}
            closeOnEscape={closeOnEscape}
          >
            <Dialog.Content
              className={cn(props?.className as string)}
              overlayClassName={overlayClassName}
              zIndex={customZ || autoZ}
              id={id}
            >
              <Content {...props} />
            </Dialog.Content>
          </Dialog>
        );
      })}
    </>
  );
};

export default DialogRoot;
